import { useCallback, useEffect, useRef, useState } from 'react';

import { apiGet, apiPost } from '../lib/api_v2';
import { logError } from '../lib/logger';

const POLL_INTERVAL_MS = 1500;
// Three legs generate in parallel and the slowest (wallpapers) lands in ~20s. Well past
// that, stop asking — a wedged run must not spin a spinner forever.
const POLL_TIMEOUT_MS = 3 * 60 * 1000;

const TERMINAL_STATUSES = new Set(['succeeded', 'failed']);

function isGenerating(setup) {
  if (!setup) return true;
  return ![setup.identity_status, setup.suggestions_status, setup.avatar_status, setup.wallpaper_status].every((status) =>
    TERMINAL_STATUSES.has(status),
  );
}

export function wallpaperImageUrl(attachment) {
  // Variants land asynchronously after ingest, so a freshly generated wallpaper has an
  // empty variants map — fall back to the original rather than a blank tile.
  return attachment?.variants?.thumb?.public_url || attachment?.public_url || null;
}

// The wallpapers finish generating while the user is still naming the space, but the
// browser does not fetch one until its tile mounts — which put a second, avoidable wait
// on top of the first. Warming them here means step two paints instantly.
function preloadWallpapers(setup, warmed) {
  if (typeof Image === 'undefined') return;

  (setup?.wallpapers || []).forEach((attachment) => {
    const url = wallpaperImageUrl(attachment);
    if (!url || warmed.has(url)) return;

    warmed.add(url);
    const image = new Image();
    image.decoding = 'async';
    image.src = url;
  });
}

/**
 * Polls the AI space-setup run behind the setup flow.
 *
 * Polling rather than the signal journal: the setup row is scaffolding the user sees
 * once, and adding a journal kind means touching live_journal_controller plus a Dexie
 * schema bump. Everything the user watches *apply* — the avatar, the wallpaper, the
 * widget — rides the existing workspace/widget broadcasts instead.
 */
export function useWorkspaceSetup(workspaceId) {
  const [setup, setSetup] = useState(null);
  const [isMissing, setIsMissing] = useState(false);
  const [hasTimedOut, setHasTimedOut] = useState(false);
  const startedAtRef = useRef(Date.now());
  const timeoutRef = useRef(null);
  const warmedUrlsRef = useRef(new Set());

  const fetchSetup = useCallback(
    async function fetchSetupCallback(signal) {
      if (!workspaceId) return null;

      try {
        const response = await apiGet(`/workspaces/${workspaceId}/setup`, { signal });
        if (signal?.aborted) return null;

        if (response.status === 404) {
          setIsMissing(true);
          return null;
        }

        const data = await response.json();
        if (signal?.aborted) return null;
        if (!(response.ok && data?.ok && data.data?.workspace_setup)) return null;

        setSetup(data.data.workspace_setup);
        preloadWallpapers(data.data.workspace_setup, warmedUrlsRef.current);
        return data.data.workspace_setup;
      } catch (error) {
        if (signal?.aborted) return null;
        logError('useWorkspaceSetup', 'Failed to load workspace setup', { message: error?.message });
        return null;
      }
    },
    [workspaceId],
  );

  useEffect(
    function pollUntilTerminal() {
      if (!workspaceId) return undefined;

      const abortController = new AbortController();
      startedAtRef.current = Date.now();
      let cancelled = false;

      async function tick() {
        const next = await fetchSetup(abortController.signal);
        if (cancelled || abortController.signal.aborted) return;

        if (!isGenerating(next)) return;

        if (Date.now() - startedAtRef.current > POLL_TIMEOUT_MS) {
          setHasTimedOut(true);
          return;
        }

        timeoutRef.current = setTimeout(tick, POLL_INTERVAL_MS);
      }

      tick();

      return function stopPolling() {
        cancelled = true;
        abortController.abort();
        if (timeoutRef.current) clearTimeout(timeoutRef.current);
      };
    },
    [fetchSetup, workspaceId],
  );

  // "Generate more" — kick a new batch, then resume polling for it. The optimistic
  // status flip is what puts skeleton tiles up before the first poll comes back.
  const generateMoreWallpapers = useCallback(
    async function generateMoreWallpapersCallback() {
      if (!workspaceId) return;

      setSetup((current) => (current ? { ...current, wallpaper_status: 'generating' } : current));

      try {
        const response = await apiPost(`/workspaces/${workspaceId}/setup/wallpapers`);
        const data = await response.json();
        if (response.ok && data?.ok && data.data?.workspace_setup) {
          setSetup(data.data.workspace_setup);
          preloadWallpapers(data.data.workspace_setup, warmedUrlsRef.current);
        } else {
          setSetup((current) => (current ? { ...current, wallpaper_status: 'succeeded' } : current));
          return;
        }
      } catch (error) {
        logError('useWorkspaceSetup', 'Failed to request more wallpapers', { message: error?.message });
        setSetup((current) => (current ? { ...current, wallpaper_status: 'succeeded' } : current));
        return;
      }

      startedAtRef.current = Date.now();
      const abortController = new AbortController();

      async function tick() {
        const next = await fetchSetup(abortController.signal);
        if (abortController.signal.aborted) return;
        if (!isGenerating(next)) return;

        if (Date.now() - startedAtRef.current > POLL_TIMEOUT_MS) {
          setHasTimedOut(true);
          return;
        }

        timeoutRef.current = setTimeout(tick, POLL_INTERVAL_MS);
      }

      tick();
    },
    [fetchSetup, workspaceId],
  );

  return {
    setup,
    isMissing,
    hasTimedOut,
    isGenerating: isGenerating(setup),
    refresh: fetchSetup,
    generateMoreWallpapers,
  };
}

export default useWorkspaceSetup;
